import { LANGUAGE_INFO, Language } from "@salon/types";
import { Check, ChevronDown, Globe, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguagePreference } from "@/hooks/use-language-preference";

interface LanguageSelectorProps {
  variant?: "default" | "ghost" | "outline";
  size?: "default" | "sm" | "lg";
  showIcon?: boolean;
  showLabel?: boolean;
  className?: string;
}

export function LanguageSelector({
  variant = "ghost",
  size = "default",
  showIcon = true,
  showLabel = false,
  className,
}: LanguageSelectorProps) {
  const { t } = useTranslation("common");
  const { currentLanguage, changeLanguage, isSaving } = useLanguagePreference();

  const supportedLanguages = Object.values(Language).map((lang) => ({
    code: lang,
    name: LANGUAGE_INFO[lang].name,
    nativeName: LANGUAGE_INFO[lang].nativeName,
  }));

  const handleLanguageChange = async (languageCode: string) => {
    const language = languageCode as Language;
    await changeLanguage(language, true); // Save to backend
  };

  const currentLanguageInfo = supportedLanguages.find((lang) => lang.code === currentLanguage);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`gap-2 ${className || ""}`}
          disabled={isSaving}
        >
          {isSaving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            // When label is hidden (compact/collapsed), prefer a flag glyph if available
            showIcon &&
            ((LANGUAGE_INFO as any)[currentLanguage]?.flag && !showLabel ? (
              <span className="text-lg leading-none">
                {(LANGUAGE_INFO as any)[currentLanguage].flag}
              </span>
            ) : (
              <Globe className="h-4 w-4" />
            ))
          )}

          {/* Only render label when explicitly requested */}
          {showLabel && (
            <span>
              {currentLanguageInfo?.nativeName || t("language.label", { defaultValue: "Language" })}
            </span>
          )}

          {/* Hide dropdown chevron in very compact mode where only an icon is shown */}
          {!isSaving && showLabel && <ChevronDown className="h-3 w-3" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {supportedLanguages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            className="flex items-center justify-between cursor-pointer"
            disabled={isSaving}
          >
            <div className="flex flex-col">
              <span className="font-medium">{language.nativeName}</span>
              <span className="text-sm text-muted-foreground">{language.name}</span>
            </div>
            {currentLanguage === language.code && <Check className="h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Compact language selector for mobile/header use
 */
export function CompactLanguageSelector() {
  return <LanguageSelector variant="ghost" size="sm" showIcon={true} showLabel={false} />;
}

/**
 * Full language selector for settings pages
 */
export function FullLanguageSelector({
  variant = "outline",
  className,
}: {
  variant?: "default" | "ghost" | "outline";
  className?: string;
} = {}) {
  return (
    <LanguageSelector
      variant={variant}
      size="default"
      showIcon={true}
      showLabel={true}
      className={className}
    />
  );
}
